<script lang="ts">
export const elInputTextTypes = ['text', 'password'] as const;
export type ElInputTextType = (typeof elInputTextTypes)[number];
</script>

<script lang="ts" setup>
import { useInput, showRequiredMark, TextualValueType, withTextualElInputProps } from '@/forms/input';
import { computed, onMounted, PropType, ref } from 'vue';
import ElIcon, { ElIconProps } from '@/ElIcon.vue';
import ElInputContainer from '@/forms/ElInputContainer.vue';
import ElIconButton from '@/ElIconButton.vue';

const props = defineProps({
  ...withTextualElInputProps(),
  type: {
    type: String as PropType<ElInputTextType>,
    default: 'text',
  },
  leadingIcon: {
    type: Object as PropType<ElIconProps>,
    default: undefined,
    required: false,
  },
  trailingIcon: {
    type: Object as PropType<ElIconProps>,
    default: undefined,
    required: false,
  },
  focusOnMount: {
    type: Boolean,
    default: false,
  },
  autocomplete: {
    type: String,
    default: 'off',
  },
});

const inputType = ref<ElInputTextType>(props.type);
const inputRef = ref<HTMLInputElement | null>(null);

onMounted(() => {
  if (props.focusOnMount) {
    inputRef.value?.focus();
  }
});

const emit = defineEmits<{
  (event: 'update:modelValue', value: TextualValueType): void;
}>();

const computedModelValue = computed(() => props.modelValue);
const computedValidation = computed(() => [props.validation]);

const { value, errorMessage, uuid, fieldContext } = useInput(computedModelValue, computedValidation, props.name, newValue => {
  emit('update:modelValue', newValue?.trim());
});

const defaultId = computed(() => (props.label ? props.label.trim().replaceAll(' ', '') : props.type));

const handleEyesPasswordClick = () => {
  inputType.value = inputType.value === 'text' ? 'password' : 'text';
};
</script>

<template>
  <ElInputContainer
    v-bind="$attrs"
    :name="id || `${uuid}-${defaultId}`"
    :label="label"
    :error-message="props.errorMessage === false ? false : props.errorMessage || errorMessage"
    :hidden-error-message="hiddenErrorMessage"
    :set-required-mark="showRequiredMark([validation])"
    class="relative"
  >
    <template #leading="{ error }">
      <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
        <div v-if="leadingIcon">
          <ElIcon
            :name="leadingIcon.name"
            :solid="leadingIcon.solid"
            class="h-5 w-5"
            aria-hidden="true"
            :class="{
              'text-neutral-lighter': !error,
              'text-error': error,
            }"
          />
        </div>
      </div>

      <slot name="leading" v-bind="{ error }" />

      <input
        :id="id || `${uuid}-${defaultId}`"
        ref="inputRef"
        v-model="value"
        :name="props.name || id || `${uuid}-${defaultId}`"
        :type="inputType"
        class="el-input truncate"
        :class="{
          error,
          '!pl-10': leadingIcon?.name,
          '!pl-3': !leadingIcon?.name,
          '!pr-10': trailingIcon?.name,
        }"
        :placeholder="placeholder"
        :disabled="disabled"
        :autocomplete="autocomplete"
        :data-cy="`${defaultId}-input`"
        @blur="fieldContext.validate()"
      />
    </template>

    <template #trailing="{ error }">
      <span v-if="trailingIcon">
        <ElIcon
          :name="trailingIcon.name"
          :solid="trailingIcon.solid"
          class="h-5 w-5"
          aria-hidden="true"
          :class="{
            'text-neutral-lighter': !error,
            'text-error': error,
          }"
        />
      </span>

      <span v-if="type === 'password'">
        <ElIconButton
          :error="error"
          :icon="{
            name: inputType === 'text' ? 'EyeSlashIcon' : 'EyeIcon',
          }"
          type="button"
          :hide-tooltip-on-change="true"
          :tooltip="inputType === 'text' ? 'Nascondi' : 'Mostra'"
          @click="handleEyesPasswordClick"
        />
      </span>

      <slot name="trailing" v-bind="{ error }" />
    </template>
  </ElInputContainer>
</template>
